const CART_SECTIONS = 'cart-items,cart-icon-bubble,cart-footer,cart-drawer';
const LEGACY_AUTO_GIFT_PROPERTY = '_cart_offer_auto_gift';
const AUTO_ADDON_GIFT_PROPERTY = '_cart_addon_auto_gift';

class CartOfferProgress extends HTMLElement {
  #syncQueued = false;

  connectedCallback() {
    document.addEventListener('cart:update', this.#handleCartUpdate);
    this.#queueGiftSync();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    const source = event.detail?.data?.source;

    // Reconcile after customer-driven cart changes, but do not react to the
    // update dispatched by this component after it adds/removes the gift.
    if (source === 'cart-addon-gift' || source === 'cart-auto-gift-cleanup') return;
    if (event.detail?.data?.didError) return;

    this.#queueGiftSync();
  };

  #queueGiftSync() {
    if (this.#syncQueued) return;

    this.#syncQueued = true;
    queueMicrotask(() => {
      this.#syncQueued = false;
      this.#syncGiftQuantity();
    });
  }

  async #syncGiftQuantity() {
    if (window.cartOfferGiftSyncPromise) {
      await window.cartOfferGiftSyncPromise.catch(() => {});
      if (this.isConnected) this.#queueGiftSync();
      return;
    }

    window.cartOfferGiftSyncPromise = (async () => {
      await this.#removeLegacyAutoGiftBoxes();
      await this.#reconcileAddonGift();
    })();

    try {
      await window.cartOfferGiftSyncPromise;
    } catch (error) {
      console.error('Unable to remove an auto-added gift box:', error);
    } finally {
      window.cartOfferGiftSyncPromise = null;
    }
  }

  async #removeLegacyAutoGiftBoxes() {
    const root = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!cartResponse.ok) return;

    const cart = await cartResponse.json();
    const autoGiftLines = cart.items.filter(
      (item) => String(item.properties?.[LEGACY_AUTO_GIFT_PROPERTY]) === 'true'
    );
    if (autoGiftLines.length === 0) return;

    this.#setGiftLoading('removing');

    try {
      const updates = Object.fromEntries(autoGiftLines.map((item) => [item.key, 0]));
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Unable to remove the previously auto-added gift box:', error);
        return;
      }

      const sectionsResponse = await fetch(`${root}cart?sections=${CART_SECTIONS}`, {
        headers: { Accept: 'application/json' },
      });
      if (!sectionsResponse.ok) return;

      const sections = await sectionsResponse.json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'cart-auto-gift-cleanup',
              sections,
            },
          },
        })
      );
    } finally {
      this.#setGiftLoading();
    }
  }

  async #reconcileAddonGift() {
    const variantId = Number.parseInt(this.dataset.addonGiftVariantId || '', 10);
    const threshold = this.#getPresentmentThreshold(this.dataset.addonGiftThreshold);

    if (!Number.isInteger(variantId) || threshold <= 0) return;

    const root = window.Shopify?.routes?.root || '/';
    const cartResponse = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!cartResponse.ok) return;

    const cart = await cartResponse.json();
    const giftLines = cart.items.filter((item) => Number(item.variant_id) === variantId);
    const autoGiftLines = giftLines.filter(
      (item) => String(item.properties?.[AUTO_ADDON_GIFT_PROPERTY]) === 'true'
    );
    const hasManualGift = giftLines.some(
      (item) => String(item.properties?.[AUTO_ADDON_GIFT_PROPERTY]) !== 'true'
    );
    const currentQuantity = autoGiftLines.reduce((total, item) => total + item.quantity, 0);
    const qualifyingTotal = this.#getQualifyingSubtotal(cart);
    const requiredQuantity = qualifyingTotal >= threshold && !hasManualGift ? 1 : 0;

    if (currentQuantity === requiredQuantity && autoGiftLines.length <= 1) return;

    this.#setGiftLoading(requiredQuantity > currentQuantity ? 'adding' : 'removing');

    try {
      let response;

      if (autoGiftLines.length > 0) {
        const updates = Object.fromEntries(autoGiftLines.map((item) => [item.key, 0]));
        if (requiredQuantity > 0) updates[autoGiftLines[0].key] = requiredQuantity;

        response = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      } else if (requiredQuantity > 0) {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: { [AUTO_ADDON_GIFT_PROPERTY]: 'true' },
              },
            ],
          }),
        });
      } else {
        return;
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Unable to synchronize the free bracelet:', error);
        return;
      }

      const sectionsResponse = await fetch(`${root}cart?sections=${CART_SECTIONS}`, {
        headers: { Accept: 'application/json' },
      });
      if (!sectionsResponse.ok) return;

      const sections = await sectionsResponse.json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              source: 'cart-addon-gift',
              sections,
            },
          },
        })
      );
    } finally {
      this.#setGiftLoading();
    }
  }

  #setGiftLoading(action) {
    const loader = this.querySelector('.cart-offer-progress__loading');
    if (!loader) return;

    if (action) {
      loader.dataset.action = action;
      loader.hidden = false;
      this.setAttribute('aria-busy', 'true');
    } else {
      loader.hidden = true;
      loader.removeAttribute('data-action');
      this.removeAttribute('aria-busy');
    }
  }

  #getPresentmentThreshold(value) {
    const presentmentCurrencyThreshold = Number.parseInt(value || '0', 10);

    if (!Number.isInteger(presentmentCurrencyThreshold) || presentmentCurrencyThreshold <= 0) {
      return 0;
    }

    return presentmentCurrencyThreshold;
  }

  #getQualifyingSubtotal(cart) {
    const autoGiftTotal = cart.items.reduce((total, item) => {
      const isAutoGift =
        String(item.properties?.[LEGACY_AUTO_GIFT_PROPERTY]) === 'true' ||
        String(item.properties?.[AUTO_ADDON_GIFT_PROPERTY]) === 'true';

      return isAutoGift ? total + item.final_line_price : total;
    }, 0);

    return Math.max(cart.total_price - autoGiftTotal, 0);
  }
}

if (!customElements.get('cart-offer-progress')) {
  customElements.define('cart-offer-progress', CartOfferProgress);
}
